import React, { useState, useEffect, useCallback } from 'react'
import { Calendar, Car, User, Building, Shield, FileText, Euro, Check, X, AlertTriangle, Eye, MapPin, Copy, ExternalLink, MessageSquare, Search } from 'lucide-react'
import { toast } from 'sonner'
import GarantiesTagsInput from './GarantiesTagsInput'

import EligibiliteAmiableBlock from './EligibiliteAmiableBlock'

const API = process.env.REACT_APP_BACKEND_URL

const RESPONSABILITES = [
  { value: 'responsable', label: 'Responsable' },
  { value: 'non_responsable', label: 'Non responsable' },
  { value: 'partage_50_50', label: 'Partage 50/50' },
]

export const CONSTATS = [
  { value: 'cosigne', label: 'Constat co-signe' },
  { value: 'non_cosigne', label: 'Constat non co-signe' },
  { value: 'mal_rempli', label: 'Constat mal rempli' },
  { value: 'absent', label: 'Absent / Non fourni' },
]

const RESPONSABILITES_ACTEES = [
  { value: 'responsable', label: 'Responsable' },
  { value: 'non_responsable', label: 'Non responsable' },
  { value: 'partage', label: 'Partage' },
  { value: 'sans_suite', label: 'Sans suite' },
]

const GARANTIES_CONTROLE = [
  { value: 'DTA', label: 'DTA' },
  { value: 'RC_MATERIEL', label: 'RC Matériel' },
  { value: 'RC_CORPOREL', label: 'RC Corporel' },
  { value: 'BRIS_DE_GLACE', label: 'Bris de glace' },
  { value: 'VOL', label: 'Vol' },
  { value: 'INCENDIE', label: 'Incendie' },
  { value: 'ASSISTANCE', label: 'Assistance' },
  { value: 'DOM', label: 'DOM' },
  { value: 'DPR', label: 'DPR' },
  { value: 'IRCA', label: 'IRCA' },
  { value: 'IRSA', label: 'IRSA' },
  { value: 'MARCHANDISES', label: 'Marchandises' },
  { value: 'PJ', label: 'Protection juridique' },
]
const GARANTIE_LABEL = Object.fromEntries(GARANTIES_CONTROLE.map(g => [g.value, g.label]))
const RESP_TO_IRSA = { responsable: 4, partage: 2, partage_50_50: 2, non_responsable: 0 }
const IRSA_LABELS = { 0: '0 — Non responsable', 2: '2 — Partage (50%)', 4: '4 — Responsable (100%)' }

// Generic inline editable field
function InlineField({ label, value, field, sinistreId, onRefresh, type = 'text', options, icon, placeholder, extra }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value || '')
  const [saving, setSaving] = useState(false)

  useEffect(() => { setVal(value || '') }, [value])

  async function save() {
    if (val === (value || '')) { setEditing(false); return }
    setSaving(true)
    try {
      const payload = { ...(extra || {}) }
      payload[field] = type === 'number' ? (val ? parseFloat(val) : null)
        : type === 'integer' ? (val !== '' && val != null ? parseInt(val, 10) : null)
        : (type === 'boolean' ? val === 'true' : val || null)
      const res = await fetch(`${API}/api/sinistres/${sinistreId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (res.ok) { onRefresh(); setEditing(false) }
      else { const d = await res.json(); toast.error(d.detail || 'Erreur') }
    } catch { toast.error('Erreur de sauvegarde') }
    finally { setSaving(false) }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') save()
    if (e.key === 'Escape') { setVal(value || ''); setEditing(false) }
  }

  const displayValue = options
    ? options.find(o => o.value === value)?.label || value || '--'
    : value || '--'

  return (
    <div className="group" data-testid={`field-${field}`}>
      <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-0.5 flex items-center gap-1">
        {icon && <span className="text-slate-300">{icon}</span>}
        {label}
      </p>
      {!editing ? (
        <p
          onClick={() => setEditing(true)}
          className="text-sm text-slate-800 cursor-pointer hover:bg-indigo-50 rounded px-1.5 py-0.5 -mx-1.5 transition-colors min-h-[24px]"
        >
          {type === 'number' && value ? `${parseFloat(value).toLocaleString('fr-FR')} €`
            : type === 'integer' ? (value != null && value !== '' ? String(value) : '--')
            : type === 'boolean' ? (value === true || value === 'true' ? 'Oui' : value === false || value === 'false' ? 'Non' : '--')
            : type === 'date' ? (value ? new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '--')
            : displayValue}
        </p>
      ) : (
        <div className="flex items-center gap-1">
          {options ? (
            <select
              value={val}
              onChange={e => setVal(e.target.value)}
              onBlur={save}
              autoFocus
              className="flex-1 text-sm border border-indigo-300 rounded px-2 py-0.5 outline-none focus:ring-1 focus:ring-indigo-400"
            >
              <option value="">--</option>
              {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ) : type === 'boolean' ? (
            <select value={val} onChange={e => setVal(e.target.value)} onBlur={save} autoFocus
              className="flex-1 text-sm border border-indigo-300 rounded px-2 py-0.5 outline-none focus:ring-1 focus:ring-indigo-400">
              <option value="">--</option>
              <option value="true">Oui</option>
              <option value="false">Non</option>
            </select>
          ) : (
            <input
              type={type === 'number' || type === 'integer' ? 'number' : type === 'date' ? 'date' : 'text'}
              value={val}
              onChange={e => setVal(e.target.value)}
              onBlur={save}
              onKeyDown={handleKeyDown}
              autoFocus
              placeholder={placeholder}
              step={type === 'number' ? '0.01' : type === 'integer' ? '1' : undefined}
              min={type === 'integer' ? '0' : undefined}
              className="flex-1 text-sm border border-indigo-300 rounded px-2 py-0.5 outline-none focus:ring-1 focus:ring-indigo-400"
            />
          )}
          {saving && <span className="text-xs text-slate-400">...</span>}
        </div>
      )}
    </div>
  )
}

// Chauffeur: saisie libre Nom / Prenom (plus de referentiel Parc)
const DESCRIPTIONS_DEFAUT = ['Déclaration flotte', 'Pre-declaration flotte', 'Pre-declaration amiable']

// Nom + prénom conducteur sur une ligne (sans doublon si le nom contient déjà le prénom)
function conducteurNomPrenom(s) {
  const saisi = Boolean((s.chauffeur_nom_saisi || '').trim())
  const nom = (s.chauffeur_nom_saisi || s.chauffeurs?.nom || '').trim()
  const brut = ((saisi ? s.chauffeur_prenom_saisi : s.chauffeurs?.prenom) || '').trim()
  const prenom = /[A-Za-zÀ-ÿ]/.test(brut) ? brut : ''
  if (nom && prenom && !nom.toLowerCase().includes(prenom.toLowerCase())) return `${nom} ${prenom}`
  return nom || prenom
}

// Commentaire inline (responsabilite actee)
function InlineComment({ label, value, field, sinistreId, onRefresh, placeholder }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value || '')
  useEffect(() => { setVal(value || '') }, [value])
  async function save() {
    if (val === (value || '')) { setEditing(false); return }
    try {
      const res = await fetch(`${API}/api/sinistres/${sinistreId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [field]: val || null }),
      })
      if (res.ok) { onRefresh(); setEditing(false) }
    } catch { toast.error('Erreur') }
  }
  return (
    <div data-testid={`field-${field}`}>
      <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-0.5 flex items-center gap-1"><MessageSquare size={10} className="text-slate-300" /> {label}</p>
      {!editing ? (
        <p onClick={() => setEditing(true)} className="text-xs text-slate-700 whitespace-pre-wrap cursor-pointer hover:bg-indigo-50 rounded px-1.5 py-1 -mx-1.5 min-h-[24px] italic">
          {value || <span className="text-slate-300 not-italic">{placeholder}</span>}
        </p>
      ) : (
        <div>
          <textarea value={val} onChange={e => setVal(e.target.value)} autoFocus rows={2} placeholder={placeholder}
            className="w-full text-xs border border-indigo-300 rounded-md p-2 resize-none outline-none focus:ring-2 focus:ring-indigo-400" />
          <div className="flex justify-end gap-1 mt-1">
            <button onClick={() => { setVal(value || ''); setEditing(false) }} className="p-1 text-slate-400 hover:text-slate-600"><X size={12} /></button>
            <button onClick={save} className="px-2 py-0.5 bg-indigo-600 text-white text-[11px] rounded hover:bg-indigo-700 flex items-center gap-1"><Check size={10} /> OK</button>
          </div>
        </div>
      )}
    </div>
  )
}

// Garanties attendues: tags libres persistes via PATCH
function GarantiesField({ sinistre, sinistreId, onRefresh, tenantId }) {
  const [saving, setSaving] = useState(false)
  async function save(next) {
    setSaving(true)
    try {
      const res = await fetch(`${API}/api/sinistres/${sinistreId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ garanties_attendues: next.length ? next : null }),
      })
      if (res.ok) onRefresh()
    } catch { toast.error('Erreur de sauvegarde') }
    finally { setSaving(false) }
  }
  return (
    <div data-testid="field-garanties_attendues" className={saving ? 'opacity-60' : ''}>
      <GarantiesTagsInput value={sinistre.garanties_attendues || []} onChange={save} tenantId={tenantId} compact testId="garanties-attendues-tags" />
    </div>
  )
}

// Etape 2 : responsabilite actee — choix explicite du gestionnaire apres validation compagnie (modifiable / annulable a tout moment)
function ActeeSelect({ value, sinistreId, onRefresh }) {
  const [saving, setSaving] = useState(false)
  async function change(e) {
    const v = e.target.value || null
    if (v === (value || null)) return
    setSaving(true)
    try {
      const res = await fetch(`${API}/api/sinistres/${sinistreId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ responsabilite_actee: v }),
      })
      if (res.ok) { toast.success(v ? 'Responsabilité actée enregistrée' : 'Responsabilité remise à « non actée »'); onRefresh() }
      else { const d = await res.json().catch(() => ({})); toast.error(typeof d.detail === 'string' ? d.detail : 'Erreur') }
    } catch { toast.error('Erreur de sauvegarde') }
    finally { setSaving(false) }
  }
  return (
    <select value={value || ''} onChange={change} disabled={saving} data-testid="field-responsabilite_actee-select"
      className={`w-full text-sm border rounded-lg px-2 py-1.5 bg-white outline-none focus:ring-2 focus:ring-blue-400 ${value ? 'border-blue-300 font-medium text-slate-800' : 'border-dashed border-slate-300 text-slate-500'}`}>
      <option value="">Non actée — en attente compagnie</option>
      {RESPONSABILITES_ACTEES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
}

// Responsabilite en 3 etapes : Supposee -> Actee -> Relevee compagnie
function ResponsabiliteStepper({ sinistre, sinistreId, onRefresh }) {
  const supposee = (sinistre.responsabilite_supposee || []).map(v => RESPONSABILITES.find(r => r.value === v)?.label || v)
  const actee = sinistre.responsabilite_actee
  const acteeLabel = RESPONSABILITES_ACTEES.find(r => r.value === actee)?.label
  const acteeIrsa = RESP_TO_IRSA[actee]
  const releve = sinistre.releve_responsabilite
  const hasReleve = releve != null
  const ecart = acteeIrsa != null && hasReleve && Number(releve) !== acteeIrsa
  const ok = acteeIrsa != null && hasReleve && Number(releve) === acteeIrsa

  const steps = [
    { key: 'supposee', title: '1. Supposée', sub: 'Gestionnaire à l\'ouverture', done: supposee.length > 0, tone: 'indigo' },
    { key: 'actee', title: '2. Actée', sub: 'Après échange compagnie', done: !!actee, tone: 'blue' },
    { key: 'releve', title: '3. Relevé compagnie', sub: sinistre.releve_date_import ? `Import du ${new Date(sinistre.releve_date_import).toLocaleDateString('fr-FR')}` : 'En attente d\'import', done: hasReleve, tone: ecart ? 'red' : 'emerald' },
  ]
  const toneCls = {
    indigo: 'border-indigo-300 bg-indigo-50', blue: 'border-blue-300 bg-blue-50',
    emerald: 'border-emerald-300 bg-emerald-50', red: 'border-red-400 bg-red-50',
  }

  return (
    <div data-testid="responsabilite-stepper">
      <div className="grid grid-cols-3 gap-3">
        {steps.map((st, i) => (
          <div key={st.key} className={`relative rounded-xl border-2 p-3 ${st.done ? toneCls[st.tone] : 'border-dashed border-slate-200 bg-white'}`} data-testid={`resp-step-${st.key}`}>
            {i < 2 && <span className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 text-slate-300 text-lg">›</span>}
            <p className="text-[11px] font-bold text-slate-700 flex items-center gap-1.5">
              {st.done ? <Check size={12} className={st.tone === 'red' ? 'text-red-500' : 'text-emerald-500'} /> : <span className="w-3 h-3 rounded-full border border-slate-300 inline-block" />}
              {st.title}
            </p>
            <p className="text-[10px] text-slate-400 mb-2">{st.sub}</p>

            {st.key === 'supposee' && (
              <MultiCheckField label="" value={sinistre.responsabilite_supposee || []} field="responsabilite_supposee" sinistreId={sinistreId} onRefresh={onRefresh} options={RESPONSABILITES} />
            )}
            {st.key === 'actee' && (
              <div className="space-y-2">
                <ActeeSelect value={actee} sinistreId={sinistreId} onRefresh={onRefresh} />
                {acteeIrsa != null && <p className="text-[10px] text-slate-500">≙ IRSA {IRSA_LABELS[acteeIrsa]}</p>}
                <InlineComment label="Commentaire" value={sinistre.responsabilite_actee_commentaire} field="responsabilite_actee_commentaire" sinistreId={sinistreId} onRefresh={onRefresh} placeholder="Ex : mail de confirmation de responsabilité reçu le ..." />
              </div>
            )}
            {st.key === 'releve' && (
              <div>
                <p className="text-sm font-medium text-slate-800" data-testid="resp-releve-value">{hasReleve ? (IRSA_LABELS[releve] || releve) : '--'}</p>
                {ecart && <span className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700" data-testid="resp-ecart-badge"><AlertTriangle size={10} /> ÉCART avec l'actée ({IRSA_LABELS[acteeIrsa]})</span>}
                {ok && <span className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700" data-testid="resp-ok-badge"><Check size={10} /> Conforme à l'actée</span>}
                {hasReleve && acteeIrsa == null && <p className="text-[10px] text-slate-400 mt-1">Renseignez la responsabilité actée pour comparer</p>}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 max-w-xs">
        <InlineField label="Cas bareme" value={sinistre.cas_bareme} field="cas_bareme" sinistreId={sinistreId} onRefresh={onRefresh} icon={<FileText size={10} />} placeholder="Ex: 51, 20..." />
      </div>
    </div>
  )
}

// Inline editable textarea for dommages
function InlineTextarea({ label, value, field, sinistreId, onRefresh, icon, labelClass = 'text-slate-400', compact = false }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value || '')
  const [saving, setSaving] = useState(false)

  useEffect(() => { setVal(value || '') }, [value])

  async function save() {
    if (val === (value || '')) { setEditing(false); return }
    setSaving(true)
    try {
      const payload = {}
      payload[field] = val || null
      const res = await fetch(`${API}/api/sinistres/${sinistreId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (res.ok) { onRefresh(); setEditing(false) }
    } catch { toast.error('Erreur') }
    finally { setSaving(false) }
  }

  return (
    <div data-testid={`field-${field}`}>
      <p className={`text-[10px] font-medium uppercase tracking-wide mb-1 flex items-center gap-1 ${labelClass}`}>
        {icon && <span className="text-slate-300">{icon}</span>} {label}
      </p>
      {!editing ? (
        <div onClick={() => setEditing(true)}
          className={`text-sm text-slate-700 whitespace-pre-wrap cursor-pointer hover:bg-indigo-50 rounded p-2 -m-1 transition-colors border border-transparent hover:border-slate-200 ${compact ? 'min-h-[36px] max-h-[72px] overflow-y-auto text-xs' : 'min-h-[48px]'}`}>
          {value || <span className="text-slate-300 italic">Cliquer pour ajouter...</span>}
        </div>
      ) : (
        <div>
          <textarea value={val} onChange={e => setVal(e.target.value)} autoFocus rows={compact ? 2 : 3}
            className="w-full text-sm border border-indigo-300 rounded-md p-2 resize-none outline-none focus:ring-2 focus:ring-indigo-400"
          />
          <div className="flex justify-end gap-1 mt-1">
            <button onClick={() => { setVal(value || ''); setEditing(false) }} className="p-1 text-slate-400 hover:text-slate-600"><X size={14} /></button>
            <button onClick={save} disabled={saving} className="px-2 py-0.5 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1">
              <Check size={12} /> {saving ? '...' : 'OK'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Multi-checkbox field for arrays (constat_options, preuve_materialite)
export function MultiCheckField({ label, value, field, sinistreId, onRefresh, options, grid = false }) {
  const [saving, setSaving] = useState(false)
  const current = Array.isArray(value) ? value : []

  async function toggle(optValue) {
    setSaving(true)
    const updated = current.includes(optValue)
      ? current.filter(v => v !== optValue)
      : [...current, optValue]
    try {
      const payload = {}
      payload[field] = updated
      const res = await fetch(`${API}/api/sinistres/${sinistreId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (res.ok) onRefresh()
    } catch { toast.error('Erreur de sauvegarde') }
    finally { setSaving(false) }
  }

  return (
    <div data-testid={`field-${field}`} className="mb-3">
      <p className={`uppercase tracking-wide mb-2 ${grid ? 'text-[11px] font-bold text-blue-600' : 'text-[10px] font-medium text-slate-400'}`}>{label}</p>
      <div className={grid ? 'grid grid-cols-2 gap-2' : 'flex flex-wrap gap-2'}>
        {options.map(o => {
          const checked = current.includes(o.value)
          return (
            <button
              key={o.value}
              onClick={() => toggle(o.value)}
              disabled={saving}
              className={`px-3 py-1.5 ${grid ? 'rounded-lg text-left' : 'rounded-full'} text-xs font-medium border transition-colors ${
                checked
                  ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                  : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
              }`}
              data-testid={`check-${field}-${o.value}`}
            >
              {checked && <Check size={10} className="inline mr-1" />}
              {o.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}


function ControleCompagnieBlock({ sinistre, sinistreId, onRefresh }) {
  const anomalies = sinistre.anomalies_compagnie || []
  const anomalieChamps = new Set(anomalies.map(a => a.champ))
  const respAttendu = RESP_TO_IRSA[sinistre.responsabilite_actee] ?? RESP_TO_IRSA[sinistre.responsabilite] ?? null
  const hasReleve = sinistre.releve_date_import != null

  function fmtEuro(v) {
    if (v == null) return null
    return Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €'
  }

  function StatusBadge({ champ, attendu, releve }) {
    if (anomalieChamps.has(champ)) {
      return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700" data-testid={`controle-ecart-${champ}`}><AlertTriangle size={10} /> ÉCART</span>
    }
    if (attendu != null && releve != null) {
      return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700" data-testid={`controle-ok-${champ}`}><Check size={10} /> OK</span>
    }
    return <span className="text-[10px] text-slate-300">non comparé</span>
  }

  function Row({ champ, label, attenduNode, attenduVal, releveNode, releveVal }) {
    const isEcart = anomalieChamps.has(champ)
    return (
      <tr className={`border-b border-slate-100 last:border-0 ${isEcart ? 'bg-red-50/60' : ''}`} data-testid={`controle-row-${champ}`}>
        <td className="px-4 py-2.5 text-xs font-medium text-slate-600 w-[140px]">{label}</td>
        <td className="px-4 py-2.5">{attenduNode}</td>
        <td className="px-4 py-2.5 text-xs text-slate-700">{releveNode}</td>
        <td className="px-4 py-2.5 text-right w-[110px]"><StatusBadge champ={champ} attendu={attenduVal} releve={releveVal} /></td>
      </tr>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="controle-compagnie-block">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
          <Eye size={16} className="text-orange-500" />
          Contrôle compagnie
          {anomalies.length > 0 && (
            <span className="ml-1 px-2 py-0.5 text-[10px] font-bold bg-red-100 text-red-700 rounded-full animate-pulse" data-testid="controle-anomalies-count">
              {anomalies.length} écart{anomalies.length > 1 ? 's' : ''}
            </span>
          )}
        </h2>
        <span className="text-[10px] text-slate-400">
          {hasReleve ? `Dernier relevé : ${new Date(sinistre.releve_date_import).toLocaleDateString('fr-FR')}` : 'Aucun relevé importé'}
        </span>
      </div>

      <table className="w-full">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-100">
            <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wide"></th>
            <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Votre estimation</th>
            <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Relevé compagnie</th>
            <th className="text-right px-4 py-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Statut</th>
          </tr>
        </thead>
        <tbody>
          <Row
            champ="responsabilite" label="Responsabilité (0/2/4)"
            attenduNode={
              <span className="text-xs text-slate-700">
                {respAttendu != null ? IRSA_LABELS[respAttendu] : <span className="text-slate-400 italic">Renseignez « Responsabilité actée »</span>}
              </span>
            }
            attenduVal={respAttendu}
            releveNode={sinistre.releve_responsabilite != null ? IRSA_LABELS[sinistre.releve_responsabilite] || sinistre.releve_responsabilite : '--'}
            releveVal={sinistre.releve_responsabilite}
          />
          <Row
            champ="recours" label="Recours"
            attenduNode={
              <InlineField label="" value={sinistre.recours_attendu} field="recours_attendu" sinistreId={sinistreId} onRefresh={onRefresh} type="number" icon={<Euro size={10} />} placeholder="+ à exercer / − à subir" />
            }
            attenduVal={sinistre.recours_attendu}
            releveNode={fmtEuro(sinistre.releve_recours) || '--'}
            releveVal={sinistre.releve_recours}
          />
          <Row
            champ="garanties" label="Garanties à ouvrir"
            attenduNode={
              <GarantiesField sinistre={sinistre} sinistreId={sinistreId} onRefresh={onRefresh} tenantId={sinistre.tenant_id} />
            }
            attenduVal={(sinistre.garanties_attendues || []).length > 0 ? sinistre.garanties_attendues : null}
            releveNode={
              sinistre.releve_garanties?.length ? (
                <div className="flex flex-wrap gap-1">
                  {sinistre.releve_garanties.map(g => (
                    <span key={g} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200">{GARANTIE_LABEL[g] || g}</span>
                  ))}
                </div>
              ) : '--'
            }
            releveVal={sinistre.releve_garanties}
          />
          <Row
            champ="nb_tiers" label="Nombre de tiers"
            attenduNode={
              <InlineField label="" value={sinistre.nb_tiers} field="nb_tiers" sinistreId={sinistreId} onRefresh={onRefresh} type="integer" icon={<User size={10} />} placeholder="Nb tiers..." />
            }
            attenduVal={sinistre.nb_tiers}
            releveNode={sinistre.releve_nb_tiers != null ? String(sinistre.releve_nb_tiers) : '--'}
            releveVal={sinistre.releve_nb_tiers}
          />
          <Row
            champ="cas_bareme" label="Cas barème IDA"
            attenduNode={<InlineField label="" value={sinistre.cas_bareme} field="cas_bareme" sinistreId={sinistreId} onRefresh={onRefresh} icon={<FileText size={10} />} placeholder="Ex: 51, 20..." />}
            attenduVal={sinistre.cas_bareme || null}
            releveNode={sinistre.releve_cas_bareme || '--'}
            releveVal={sinistre.releve_cas_bareme || null}
          />
          <Row
            champ="situation" label="Situation dossier"
            attenduNode={<span className="text-xs text-slate-700">{['cloture', 'cloture_favorable', 'cloture_refus', 'sans_suite', 'reglement_effectue', 'paye'].includes(sinistre.statut) ? 'Clos' : 'Ouvert'} <span className="text-slate-400">(statut Pilote)</span></span>}
            attenduVal={sinistre.statut || null}
            releveNode={sinistre.releve_situation ? <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${['CLOS', 'RECLOS'].includes(sinistre.releve_situation.toUpperCase()) ? 'bg-slate-200 text-slate-700' : 'bg-blue-100 text-blue-700'}`}>{sinistre.releve_situation}</span> : '--'}
            releveVal={sinistre.releve_situation || null}
          />
          {anomalieChamps.has('doublon_releve') && (
            <Row
              champ="doublon_releve" label="Doublon relevé"
              attenduNode={<span className="text-xs text-slate-700">1 seul dossier</span>}
              attenduVal={1}
              releveNode={<span className="text-xs font-mono text-red-700">{anomalies.find(a => a.champ === 'doublon_releve')?.releve}</span>}
              releveVal={2}
            />
          )}
        </tbody>
      </table>

      {anomalies.length > 0 && (
        <div className="mx-4 mb-4 mt-1 border border-red-200 bg-red-50 rounded-lg p-3" data-testid="controle-anomalies-detail">
          <p className="text-[11px] font-bold text-red-700 mb-1.5 flex items-center gap-1.5">
            <AlertTriangle size={12} /> Écarts détectés avec le relevé compagnie — à traiter avec la compagnie
          </p>
          <ul className="space-y-1">
            {anomalies.map((a, i) => (
              <li key={i} className="text-[11px] text-red-600">
                <span className="font-semibold">{a.label}</span> : attendu <span className="font-mono">{a.attendu}</span> — relevé <span className="font-mono">{a.releve}</span>
                {a.detail && <span className="block text-[10px] text-red-500 ml-2">{a.detail}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}


export default function FlotteSinistreBlocks({ sinistre, sinistreId, onRefresh, isAmiable, financeSlot = null }) {
  const isFlotte = sinistre?.type_sinistre === 'flotte' || sinistre?.polices?.type_police === 'flotte'

  if (!isFlotte) return null

  function fmtDate(d) {
    if (!d) return '--'
    try { return new Date(d).toLocaleDateString('fr-FR') } catch { return d }
  }


  return (
    <div className="space-y-4" data-testid="flotte-blocks">
      {/* Alerte doublon suspecte a l'ouverture */}
      {Array.isArray(sinistre.doublons_suspects) && sinistre.doublons_suspects.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4" data-testid="alert-doublon-suspect">
          <div className="flex items-start gap-3">
            <Copy size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">
                Doublon suspecté à l'ouverture {sinistre.doublon_confirme ? '— ouverture confirmée par le gestionnaire' : ''}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {sinistre.doublons_suspects.map(d => (
                  <a key={d.id} href={`/c2/sinistre/${d.id}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-amber-200 rounded-lg text-xs hover:border-amber-400" data-testid={`doublon-suspect-link-${d.id}`}>
                    <span className="font-mono font-bold text-indigo-700">{d.numero_sinistre_client || d.numero_sinistre}</span>
                    <span className="text-slate-500">{(d.motifs || []).join(', ')}</span>
                    <ExternalLink size={10} className="text-slate-400" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bloc unifié : Informations du sinistre (assurance flotte) — masque en amiable (affiché dans AmiableSinistreView) */}
      {!isAmiable && (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="flotte-info-sinistre-block">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
            <Car size={16} className="text-indigo-500" />
            Informations du sinistre
            <span className="ml-2 px-2 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-700 rounded-full uppercase">Assurance</span>
            {sinistre.est_mise_en_cause && (
              <span className="px-2 py-0.5 text-[10px] font-bold bg-red-100 text-red-700 rounded-full border border-red-200" data-testid="info-badge-mec">MEC</span>
            )}
            {(sinistre.est_conservatoire != null ? sinistre.est_conservatoire : !sinistre.numero_dossier_compagnie) && (
              <span className="px-2 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700 rounded-full border border-amber-200" data-testid="info-badge-cons">CONS</span>
            )}
          </h2>
        </div>
        <div className="p-5 space-y-4">
          {/* Ligne 1 : références */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-4" data-testid="flotte-sb-references">
            <div>
              <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-0.5 flex items-center gap-1">
                <span className="text-slate-300"><Shield size={10} /></span> Police
              </p>
              <p className="text-sm text-slate-800 font-medium">{sinistre.polices?.numero_police || '--'}</p>
            </div>
            <InlineField label="N° d'affaire" value={sinistre.numero_affaire} field="numero_affaire" sinistreId={sinistreId} onRefresh={onRefresh} icon={<FileText size={10} />} />
            <InlineField label="N° de dossier Cie" value={sinistre.numero_dossier_compagnie} field="numero_dossier_compagnie" sinistreId={sinistreId} onRefresh={onRefresh} icon={<Building size={10} />} />
            <InlineField label="N° sinistre client" value={sinistre.numero_sinistre_client} field="numero_sinistre_client" sinistreId={sinistreId} onRefresh={onRefresh} icon={<FileText size={10} />} placeholder="CLT-..." />
          </div>

          {/* Survenance + véhicule */}
          <div className="pt-4 border-t-2 border-blue-600 space-y-4" data-testid="flotte-sb-survenance">
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
              <InlineField label="Date de survenance" value={sinistre.date_fait} field="date_fait" sinistreId={sinistreId} onRefresh={onRefresh} type="date" icon={<Calendar size={10} />} />
              <InlineField label="Lieu" value={sinistre.lieu || sinistre.lieu_sinistre?.adresse || ''} field="lieu" sinistreId={sinistreId} onRefresh={onRefresh} icon={<MapPin size={10} />} placeholder="Lieu du sinistre..." />
              <InlineField label="Dépôt" value={sinistre.nom_chef_depot} field="nom_chef_depot" sinistreId={sinistreId} onRefresh={onRefresh} icon={<Building size={10} />} />
            </div>
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
              <InlineField label="Immat" value={(sinistre.immatriculation_saisie || sinistre.vehicules?.immatriculation || '').replace(/-/g, '')} field="immatriculation_saisie" sinistreId={sinistreId} onRefresh={onRefresh} icon={<Car size={10} />} placeholder="AB123CD" />
              <InlineField label="Modèle" value={sinistre.modele_saisi || (sinistre.vehicules ? `${sinistre.vehicules.marque || ''} ${sinistre.vehicules.modele || ''}`.trim() : '')} field="modele_saisi" sinistreId={sinistreId} onRefresh={onRefresh} icon={<Car size={10} />} placeholder="Marque modele" />
              <InlineField label="Nom Prénom conducteur" value={conducteurNomPrenom(sinistre)} field="chauffeur_nom_saisi" sinistreId={sinistreId} onRefresh={onRefresh} icon={<User size={10} />} placeholder="NOM Prénom" extra={{ chauffeur_prenom_saisi: null }} />
            </div>
          </div>

          {/* Description */}
          <div className="pt-4 border-t-2 border-blue-600" data-testid="flotte-sb-description">
            <InlineTextarea label="Description" labelClass="text-blue-600 font-bold" value={DESCRIPTIONS_DEFAUT.includes(sinistre.description) ? '' : sinistre.description} field="description" sinistreId={sinistreId} onRefresh={onRefresh} />
          </div>

          {/* Dates compagnie */}
          <div className="pt-4 border-t-2 border-blue-600 grid grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-4" data-testid="flotte-sb-dates">
            <InlineField label="Date de réception MEC" value={sinistre.date_reception_mec} field="date_reception_mec" sinistreId={sinistreId} onRefresh={onRefresh} type="date" icon={<Calendar size={10} />} />
            <InlineField label="Date de réception déclaration" value={sinistre.date_reception_declaration} field="date_reception_declaration" sinistreId={sinistreId} onRefresh={onRefresh} type="date" icon={<Calendar size={10} />} />
            <InlineField label="Date de transmission Cie" value={sinistre.date_transmission_compagnie} field="date_transmission_compagnie" sinistreId={sinistreId} onRefresh={onRefresh} type="date" icon={<Calendar size={10} />} />
            <InlineField label="Date ouverture Cie" value={sinistre.date_ouverture_compagnie} field="date_ouverture_compagnie" sinistreId={sinistreId} onRefresh={onRefresh} type="date" icon={<Calendar size={10} />} />
          </div>

          {/* Tiers */}
          <div className="pt-4 border-t-2 border-blue-600" data-testid="flotte-sb-tiers">
            <p className="text-[11px] font-bold text-blue-600 uppercase tracking-wide mb-3">Tiers</p>
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
              <InlineField label="Nom Prénom / Société" value={sinistre.tiers_nom || sinistre.info_tiers} field="tiers_nom" sinistreId={sinistreId} onRefresh={onRefresh} icon={<User size={10} />} />
              <InlineField label="Immat" value={sinistre.immatriculation_tiers} field="immatriculation_tiers" sinistreId={sinistreId} onRefresh={onRefresh} icon={<Car size={10} />} />
              <InlineField label="Cie Assurance" value={sinistre.tiers_assurance} field="tiers_assurance" sinistreId={sinistreId} onRefresh={onRefresh} icon={<Shield size={10} />} />
            </div>
          </div>

          {/* Dommages */}
          <div className="pt-4 border-t-2 border-blue-600" data-testid="flotte-sb-dommages">
            <p className="text-[11px] font-bold text-blue-600 uppercase tracking-wide mb-3">Dommages</p>
            <div className="grid grid-cols-2 gap-6">
              <InlineTextarea label="Dommages vehicule client" value={sinistre.dommages_client} field="dommages_client" sinistreId={sinistreId} onRefresh={onRefresh} />
              <InlineTextarea label="Dommages vehicule tiers" value={sinistre.dommages_tiers} field="dommages_tiers" sinistreId={sinistreId} onRefresh={onRefresh} />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Bloc Elements : constat (2x2) + reglement amiable — masque en amiable */}
      {!isAmiable && (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="bloc-elements">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
            <FileText size={16} className="text-slate-500" />
            Elements
          </h2>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <MultiCheckField
              label="Constat"
              value={sinistre.constat_options || []}
              field="constat_options"
              sinistreId={sinistreId}
              onRefresh={onRefresh}
              options={CONSTATS}
              grid
            />
            <InlineTextarea label="Bloc-notes" value={sinistre.notes_elements} field="notes_elements" sinistreId={sinistreId} onRefresh={onRefresh} compact />
          </div>
          <div className="md:border-l-2 md:border-blue-600 md:pl-6">
            <EligibiliteAmiableBlock sinistre={sinistre} onRefresh={onRefresh} embedded />
          </div>
        </div>
      </div>
      )}

      {/* Bloc Evaluation financiere — sous Elements (flotte assurance) */}
      {!isAmiable && (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="bloc-evaluation-financiere">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
            <Euro size={16} className="text-emerald-500" />
            Evaluation Financiere (Assurance)
          </h2>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-3 gap-x-6 gap-y-4">
            <InlineField label="Montant devis" value={sinistre.montant_devis} field="montant_devis" sinistreId={sinistreId} onRefresh={onRefresh} type="number" icon={<Euro size={10} />} />
            <InlineField label="Montant facture" value={sinistre.montant_facture} field="montant_facture" sinistreId={sinistreId} onRefresh={onRefresh} type="number" icon={<Euro size={10} />} />
            <InlineField label="Montant rapport expert" value={sinistre.montant_rapport_expertise} field="montant_rapport_expertise" sinistreId={sinistreId} onRefresh={onRefresh} type="number" icon={<Euro size={10} />} />
          </div>
          <div className="mt-4 pt-4 border-t-2 border-slate-300 grid grid-cols-3 gap-x-6 gap-y-4">
            <InlineField label="Somme versée" value={sinistre.montant_rembourse} field="montant_rembourse" sinistreId={sinistreId} onRefresh={onRefresh} type="number" icon={<Euro size={10} />} />
            <div className="col-span-2">
              <InlineTextarea label="Bloc-notes" value={sinistre.notes_financieres} field="notes_financieres" sinistreId={sinistreId} onRefresh={onRefresh} />
            </div>
          </div>
          {financeSlot && (
            <div className="mt-4 pt-4 border-t-2 border-slate-300" data-testid="finance-slot">{financeSlot}</div>
          )}
        </div>
      </div>
      )}

      {/* Bloc Responsabilite — masque en amiable */}
      {!isAmiable && (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
            <Shield size={16} className="text-indigo-500" />
            Responsabilite — suivi en 3 étapes
          </h2>
        </div>
        <div className="p-5">
          <ResponsabiliteStepper sinistre={sinistre} sinistreId={sinistreId} onRefresh={onRefresh} />
        </div>
      </div>
      )}

      {/* Bloc Controle compagnie — flotte assurance uniquement */}
      {!isAmiable && (
        <ControleCompagnieBlock sinistre={sinistre} sinistreId={sinistreId} onRefresh={onRefresh} />
      )}

    </div>
  )
}

// Bloc Expertise — colonne laterale de la fiche assurance flotte
export function ExpertiseBlock({ sinistre, sinistreId, onRefresh }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="bloc-expertise">
      <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
        <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
          <Search size={16} className="text-violet-500" />
          Expertise
        </h2>
      </div>
      <div className="p-5 space-y-4">
        <InlineField label="Date mission expert" value={sinistre.date_mission_expert} field="date_mission_expert" sinistreId={sinistreId} onRefresh={onRefresh} type="date" icon={<Calendar size={10} />} />
        <InlineField label="Date RDV expertise" value={sinistre.date_rdv_expertise} field="date_rdv_expertise" sinistreId={sinistreId} onRefresh={onRefresh} type="date" icon={<Calendar size={10} />} />
        <InlineTextarea label="Informations expertise" value={sinistre.informations_expertise} field="informations_expertise" sinistreId={sinistreId} onRefresh={onRefresh} />
      </div>
    </div>
  )
}
